/**
 * useContent — consumer hook resolving the active `ContentReaderPort`
 * adapter from `PortsContext`.
 *
 * Hook colocation: lives under `apps/web/src/content/hooks/` alongside
 * the upcoming TA16 per-method hooks (`useCategories`, `useArticle`,
 * `useCuratedCards`). This mirrors the
 * `apps/web/src/session/useSession.ts` pattern (consumer hook in the
 * domain module, not in `providers/`) — Cluster 1's stub originally
 * sat in `providers/useContent.ts` because there was no domain module
 * yet; this task creates it.
 *
 * Cluster 1 missing-port stub semantics are preserved: when the
 * `PortsProvider` was either not mounted (`PortsContext` value is
 * `null`) or mounted without a `content` adapter (slot is `undefined`),
 * the hook throws a clear `Error` with the same message the existing
 * stub used. The throwing-stub case (port BOUND but every property
 * access throws `NotImplementedError`) is handled by the consumer of
 * `port` itself — when a route component reads `port.listCategories(...)`,
 * the `Proxy` get-trap surfaces `NotImplementedError` and the
 * `PortErrorBoundary` (T022) catches it. Those are two distinct
 * conditions and they intentionally throw distinct error shapes.
 *
 * No subscription wrapper here: `ContentReaderPort` is a request/response
 * port, not a state-stream port like `SessionPort`. Per-method hooks
 * (TA16) wrap the returned port in React Query for caching + loading
 * states; this consumer hook is the one-line resolver they all share.
 *
 * Authoritative spec reference:
 *   - `specs/011-pwa-prototype-convergence/data-model.md` §ContentReaderPort
 *   - `specs/003-content-and-knowledge/tasks.md` TA14 (this hook) +
 *     TA16 (per-method hooks built on top of it).
 */

import { useContext } from 'react';

import { PortsContext } from '../../providers/PortsContext.js';
import type { ContentReaderPort } from '../ContentReaderPort.js';

export function useContent(): ContentReaderPort {
  const ports = useContext(PortsContext);
  if (ports === null || ports.content === undefined) {
    throw new Error(
      'useContent must be used within a PortsProvider that provides the content port',
    );
  }
  return ports.content;
}
